import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { Layers, IndianRupee, TrendingUp, TrendingDown, Users, Info, X, LayoutGrid, PieChart, Ruler, Calendar, User, Award, ChevronRight, Building2, Home, UserCheck, Download, Target as TargetIcon } from "lucide-react";
import { SectionHeader, PageHeader, SnowflakeRequired } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import ThemeIcon from "../components/ThemeIcon";
import { buildDashboardParams, serializeFyForApi } from "../utils/dashboardFilters";
import { getThemeDotColor } from "../utils/dynamicColors";
import InfoTooltip from "../components/InfoTooltip";
import { fmtCr, fmtCrNoSymbol, fmtNum, fmtOutreach, displayTheme } from "../utils/format";
import GrantsTableSection from "../components/GrantsTableSection";
import { fabricateThemeKpis, summariseKpiStatuses } from "../utils/themeKpiTemplates";

const STATUS_COLORS = {
  "Ongoing": { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", dot: "bg-green-500" },
  "Active": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  "Closed": { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200", dot: "bg-slate-400" },
  "Under Closure": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
};
const getStatusStyle = (s) => STATUS_COLORS[s] || { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", dot: "bg-slate-400" };

const ProgressBar = ({ label, actual, target, color, gradientFrom, gradientTo }) => {
  const pct = target > 0 ? ((actual / target) * 100) : 0;
  const clampedPct = Math.min(pct, 100);
  const displayPct = pct.toFixed(1);
  const badgeColor = pct >= 80 ? "bg-green-100 text-green-700" : pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  const gFrom = gradientFrom || color || "#006837";
  const gTo = gradientTo || color || "#E53935";
  const gradId = `grad-${label.replace(/\s/g, '')}`;
  return (
    <div className="space-y-1.5" data-testid={`progress-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold text-slate-700">{label}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}>{displayPct}%</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden shadow-inner">
        <svg width="100%" height="12">
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={gFrom} />
              <stop offset="100%" stopColor={gTo} />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={`${clampedPct}%`} height="12" rx="6" fill={`url(#${gradId})`} className="transition-all duration-700" />
        </svg>
      </div>
      <div className="flex justify-between text-[10px] text-slate-600">
        <span>Actual: <span className="font-medium text-slate-600">{fmtNum(actual)}</span></span>
        <span>Target: <span className="font-medium text-slate-600">{fmtNum(target)}</span></span>
      </div>
    </div>
  );
};

const DonutChart = ({ pct, size = 56, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const offset = circumference - (clamped / 100) * circumference;
  const color = pct >= 70 ? "#16A34A" : pct >= 40 ? "#F59E0B" : "#DC2626";
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
    </svg>
  );
};

const TILE_CONFIG = [
  { key: "grants", icon: LayoutGrid, borderColor: "#006837", iconBg: "bg-red-50", iconColor: "text-red-600" },
  { key: "sanctioned", icon: IndianRupee, borderColor: "#1E40AF", iconBg: "bg-blue-50", iconColor: "text-blue-600" },
  { key: "disbursed", icon: TrendingUp, borderColor: "#16A34A", iconBg: "bg-green-50", iconColor: "text-green-600" },
  { key: "portfolio", icon: PieChart, borderColor: "#7C3AED", iconBg: "bg-violet-50", iconColor: "text-violet-600" },
  { key: "avg_grant", icon: Ruler, borderColor: "#0891B2", iconBg: "bg-cyan-50", iconColor: "text-cyan-600" },
  { key: "awp", icon: Calendar, borderColor: "#D97706", iconBg: "bg-amber-50", iconColor: "text-amber-600" },
];

const MetricTile = ({ label, value, subtitle, tileKey }) => {
  const cfg = TILE_CONFIG.find(t => t.key === tileKey) || TILE_CONFIG[0];
  const Icon = cfg.icon;
  return (
    <div className="rounded-lg bg-white border border-slate-100 shadow-sm px-3.5 py-3 flex items-start gap-3 hover:shadow-md transition-shadow"
      style={{ borderLeft: `3px solid ${cfg.borderColor}` }}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.iconBg}`}>
        <Icon size={15} className={cfg.iconColor} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">{label}</div>
        <div className="text-[15px] font-bold text-slate-800 leading-tight mt-0.5">{value}</div>
        {subtitle && <div className="text-[10px] text-slate-600 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
};

const GeoStat = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-lg border border-slate-100 px-4 py-3 shadow-sm">
    <div className="flex items-baseline gap-1.5">
      <span className="text-xl font-bold leading-none" style={{ color }}>{value}</span>
      {sub != null && <span className="text-xs font-semibold text-slate-600">({sub} aspirational)</span>}
    </div>
    <div className="text-xs font-semibold text-slate-700 mt-1">{label}</div>
  </div>
);

const OutreachStat = ({ icon: Icon, value, label, iconBg, iconColor }) => (
  <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-100 px-3 py-2.5 shadow-sm min-w-0 overflow-hidden">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon size={14} className={iconColor} />
    </div>
    <div className="min-w-0 overflow-hidden">
      <div className="text-sm font-bold text-slate-800 leading-none truncate" title={(value || 0).toLocaleString("en-IN")}>{fmtOutreach(value)}</div>
      <div className="text-[10px] text-slate-700 font-semibold mt-0.5 truncate">{label}</div>
    </div>
  </div>
);

const ThemeDetailPanel = ({ detail, loading, onClose, themeColor, onGrantsClick }) => {
  if (loading) return (
    <div className="col-span-full bg-white border border-slate-200 rounded-xl p-6 shadow-lg animate-pulse" data-testid="theme-detail-loading">
      <div className="h-4 bg-slate-200 rounded w-1/3 mb-4" />
      <div className="grid grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-lg" />)}
      </div>
    </div>
  );
  if (!detail) return null;

  const disbColor = detail.disbursement_pct >= 70 ? "text-green-600" : detail.disbursement_pct >= 40 ? "text-amber-600" : "text-red-600";
  const disbBadge = detail.disbursement_pct >= 70 ? "bg-green-100 text-green-700" : detail.disbursement_pct >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  const accent = themeColor || "#006837";

  return (
    <div className="col-span-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="theme-detail-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100"
        style={{ background: `linear-gradient(135deg, ${accent}08 0%, transparent 60%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full" style={{ background: accent }} />
          <h3 className="text-sm font-bold text-slate-800">{displayTheme(detail.theme)}</h3>
          {/* Status chips */}
          <div className="flex gap-1.5 ml-2">
            {(detail.status_breakdown || []).map(s => {
              const st = getStatusStyle(s.status);
              return (
                <span key={s.status} className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${st.bg} ${st.text} ${st.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                  {s.status}: {s.count}
                </span>
              );
            })}
          </div>
          {/* Total grants chip - clickable */}
          <button onClick={onGrantsClick}
            className="group text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-800 text-white ml-auto mr-2 hover:bg-[#006837] hover:scale-110 hover:shadow-lg hover:ring-2 hover:ring-[#006837]/30 transition-all duration-200 cursor-pointer"
            data-testid="theme-grants-chip" title="View grants table">
            {detail.grants} Grants <span className="inline-block transition-transform duration-200 group-hover:translate-y-0.5">&darr;</span>
          </button>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-600 transition-colors" data-testid="theme-detail-close">
          <X size={16} />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Row 1: Key Metrics with icons & colored borders */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <MetricTile label="Total Sanctioned" value={fmtCr(detail.sanctioned_cr)} tileKey="sanctioned" />
          <div className="rounded-lg bg-white border border-slate-100 shadow-sm px-3.5 py-3 flex items-start gap-3 hover:shadow-md transition-shadow"
            style={{ borderLeft: "3px solid #16A34A" }}>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50">
                <TrendingUp size={15} className="text-green-600" />
              </div>
              <div className="relative">
                <DonutChart pct={detail.disbursement_pct} size={40} strokeWidth={4} />
                <span className={`absolute inset-0 flex items-center justify-center text-[8px] font-bold ${disbColor}`}>{detail.disbursement_pct}%</span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">Total Disbursed</div>
              <div className="text-[15px] font-bold text-slate-800 leading-tight mt-0.5">{fmtCr(detail.disbursed_cr)}</div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${disbBadge} hidden`}>{detail.disbursement_pct}% disbursed</span>
            </div>
          </div>
          <MetricTile label="Portfolio %" value={`${detail.portfolio_pct}%`} tileKey="portfolio" />
          <MetricTile label="Avg Grant Size" value={fmtCr(detail.avg_grant_size_cr)} tileKey="avg_grant" />
          <MetricTile label="AWP Amount" value={fmtCr(detail.awp_cr)} tileKey="awp" />
        </div>

        {/* Row 2: Geographic Coverage + Outreach side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="geo-outreach-row">
          {/* Geographic Coverage */}
          {detail.geo_coverage && (
            <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Layers size={14} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Geographic Coverage</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <GeoStat label="States & UT" value={detail.geo_coverage.states} color="#006837" />
                <GeoStat label="Districts" value={detail.geo_coverage.districts} sub={detail.geo_coverage.aspirational_districts} color="#1E40AF" />
                <GeoStat label="Blocks" value={detail.geo_coverage.blocks} sub={detail.geo_coverage.aspirational_blocks} color="#0891B2" />
              </div>
            </div>
          )}

          {/* Outreach */}
          {detail.outreach && detail.outreach.grant_count > 0 && (
            <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-4" data-testid="outreach-section">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Users size={14} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Outreach</span>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                  from {detail.outreach.grant_count} of {detail.grants} grants
                </span>
              </div>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                <OutreachStat icon={Building2} value={detail.outreach.institutions} label="Institutions" iconBg="bg-blue-50" iconColor="text-blue-600" />
                <OutreachStat icon={UserCheck} value={detail.outreach.user_groups} label="User Groups" iconBg="bg-violet-50" iconColor="text-violet-600" />
                <OutreachStat icon={Home} value={detail.outreach.households} label="Households" iconBg="bg-amber-50" iconColor="text-amber-600" />
                <OutreachStat icon={User} value={detail.outreach.individuals} label="Individuals" iconBg="bg-green-50" iconColor="text-green-600" />
              </div>
            </div>
          )}
        </div>

        {/* Row 3: Grant Anchors + Top Grantees side by side (moved to bottom) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Grant Anchors */}
          <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-3.5">
            <div className="flex items-center gap-1.5 mb-2.5">
              <User size={12} className="text-slate-600" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Grant Anchors</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {detail.po_names.map(po => (
                <span key={po.name} className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border"
                  style={{ background: `${accent}08`, borderColor: `${accent}25`, color: accent }}>
                  {po.name} <span className="text-[9px] opacity-70">({po.grants})</span>
                </span>
              ))}
              {detail.po_names.length === 0 && <span className="text-[11px] text-slate-600 italic">No Grant Anchor assigned</span>}
            </div>
          </div>

          {/* Top Grantees */}
          <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-3.5">
            <div className="flex items-center gap-1.5 mb-2.5 flex-wrap">
              <Award size={12} className="text-slate-600" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Top Grantees by Sanctioned Amount</span>
              {detail.total_grantees != null && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">
                  {detail.total_grantees} total grantees
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {(detail.top_grantees || []).map((g, i) => (
                <div key={g.name} className="flex items-center gap-2 text-[11px]">
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span className="text-slate-700 font-medium truncate flex-1">{g.name}</span>
                  <span className="text-slate-600 flex-shrink-0">{g.grants} grants</span>
                  <span className="text-slate-800 font-semibold flex-shrink-0">{fmtCr(g.sanctioned_cr)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Key Performance Indicators (KPIs) — RAG Cards */}
        <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-3.5">
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            <TrendingUp size={12} className="text-slate-600" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Key Performance Indicators (KPIs)</span>
            {detail.meas_grant_count != null && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">
                from {detail.meas_grant_count} of {detail.grants} grants
              </span>
            )}
          </div>
          {detail.rag_counts && detail.rag_counts.total > 0 ? (
            <div className="grid grid-cols-4 gap-3" data-testid="thematic-rag-kpi">
              <div className="bg-white rounded-lg border-l-4 border-blue-500 border border-slate-200 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{detail.rag_counts.blue}</div>
                <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider mt-1">Blue</div>
                <div className="text-[9px] text-slate-500 mt-0.5">&gt; 100%</div>
              </div>
              <div className="bg-white rounded-lg border-l-4 border-green-500 border border-slate-200 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-green-600">{detail.rag_counts.green}</div>
                <div className="text-[10px] font-semibold text-green-700 uppercase tracking-wider mt-1">Green</div>
                <div className="text-[9px] text-slate-500 mt-0.5">96–100%</div>
              </div>
              <div className="bg-white rounded-lg border-l-4 border-amber-500 border border-slate-200 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-amber-600">{detail.rag_counts.amber}</div>
                <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mt-1">Amber</div>
                <div className="text-[9px] text-slate-500 mt-0.5">81–95%</div>
              </div>
              <div className="bg-white rounded-lg border-l-4 border-red-500 border border-slate-200 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-red-600">{detail.rag_counts.red}</div>
                <div className="text-[10px] font-semibold text-red-700 uppercase tracking-wider mt-1">Red</div>
                <div className="text-[9px] text-slate-500 mt-0.5">0–80%</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-600 italic">No KPI data available</div>
          )}
        </div>

        {/* Row 5: Sample Programme KPIs with progress bars (illustrative) */}
        <SampleKpiSection theme={detail.theme} />
      </div>
    </div>
  );
};

// Format KPI value with unit suffix
const fmtKpiValue = (v, unit) => {
  if (v === null || v === undefined) return "–";
  if (unit === "%") return `${v}%`;
  if (unit === "ha") return `${new Intl.NumberFormat("en-IN").format(v)} ha`;
  return new Intl.NumberFormat("en-IN").format(v);
};

const SampleKpiSection = ({ theme }) => {
  const kpis = useMemo(() => fabricateThemeKpis(theme), [theme]);
  const counts = useMemo(() => summariseKpiStatuses(kpis), [kpis]);

  const ChipStat = ({ count, label, dot }) => (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      <span className="font-bold text-slate-800">{count}</span> {label}
    </span>
  );

  return (
    <div
      className="bg-slate-50/50 rounded-lg border border-slate-100 p-3.5"
      data-testid="thematic-sample-kpis"
    >
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <TargetIcon size={12} className="text-slate-600" />
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          Sample Programme KPIs
        </span>
        <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          Illustrative
        </span>
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <ChipStat count={counts.completed} label="Completed" dot="#059669" />
          <ChipStat count={counts.on_track} label="On Track" dot="#16A34A" />
          <ChipStat count={counts.at_risk} label="At Risk" dot="#D97706" />
          <ChipStat count={counts.off_track} label="Off Track" dot="#DC2626" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {kpis.map((k) => {
          const widthPct = Math.min(100, k.pct);
          const TrendIcon = k.trend === "up" ? TrendingUp : TrendingDown;
          return (
            <div
              key={k.id}
              className="bg-white rounded-lg border border-slate-200 px-3.5 py-3"
              data-testid={`sample-kpi-${k.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-slate-800 leading-snug">{k.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Achieved
                    <span className="font-bold text-slate-800 ml-1">{fmtKpiValue(k.achieved, k.unit)}</span>
                    <span className="mx-1 text-slate-400">/</span>
                    Target
                    <span className="font-medium text-slate-600 ml-1">{fmtKpiValue(k.target, k.unit)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${k.status.bg} ${k.status.color} ${k.status.border}`}
                  >
                    {k.status.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${k.trend === "up" ? "text-emerald-600" : "text-rose-600"}`}
                    title={k.trend === "up" ? "Trending up vs last period" : "Trending down vs last period"}
                  >
                    <TrendIcon size={10} />
                    {k.trend === "up" ? "Improving" : "Declining"}
                  </span>
                </div>
              </div>

              <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${widthPct}%`, background: k.status.bar }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-500">Progress</span>
                <span className="text-[10px] font-bold" style={{ color: k.status.bar }}>{k.pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function ThematicOverview() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const [summary, setSummary] = useState(null);
  const [themeCards, setThemeCards] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trustMatrix, setTrustMatrix] = useState({ data: [], trusts: [], themes: [] });
  const [heatmapToggle, setHeatmapToggle] = useState("grants");
  const sf = filterOptions.data_sources?.grants || filterOptions.data_available;

  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const fetchAll = async () => {
    setLoading(true);
    setGlobalLoading(true);
    try {
      const [sumRes, cardsRes, compRes, matrixRes] = await Promise.all([
        axios.get(`${API}/thematic/summary`, { params }),
        axios.get(`${API}/thematic/theme-cards`, { params }),
        axios.get(`${API}/thematic/comparison`, { params }),
        axios.get(`${API}/executive/trust-theme-matrix`, { params: buildDashboardParams(filters, { status: filters.status }) }).catch(() => ({ data: { data: [], trusts: [], themes: [] } })),
      ]);
      setSummary(sumRes.data);
      setThemeCards(cardsRes.data);
      setComparison(compRes.data);
      setTrustMatrix(matrixRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setGlobalLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeDetail, setThemeDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const grantsTableRef = useRef(null);

  const grantsTableFilters = useMemo(() => {
    const f = {};
    const serializedFy = serializeFyForApi(filters.fy);
    if (serializedFy) f.fy = serializedFy;
    if (viewMode) f.view_mode = viewMode;
    if (selectedTheme) f.theme = selectedTheme;
    if (filters.trust) f.trust = filters.trust;
    if (filters.status) f.status = filters.status;
    if (filters.grantee) f.grantee = filters.grantee;
    return f;
  }, [filters.fy, viewMode, selectedTheme, filters.trust, filters.status, filters.grantee]);

  const scrollToGrantsTable = () => {
    grantsTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const visibleCards = selectedTheme ? themeCards.filter(c => c.theme === selectedTheme) : themeCards;

  const handleThemeClick = async (themeName) => {
    if (selectedTheme === themeName) {
      setSelectedTheme(null);
      setThemeDetail(null);
      return;
    }
    setSelectedTheme(themeName);
    setDetailLoading(true);
    try {
      const params = buildDashboardParams(filters, { view_mode: viewMode });
      params.theme = themeName;
      const res = await axios.get(`${API}/thematic/theme-detail`, { params, withCredentials: true });
      setThemeDetail(res.data);
    } catch (e) {
      console.error("Theme detail fetch failed:", e);
      setThemeDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Reset detail panel when global filters change
  useEffect(() => {
    setSelectedTheme(null);
    setThemeDetail(null);
  }, [filters, viewMode]);

  return (
    <div data-testid="thematic-page" className="space-y-6">
      <PageHeader title="Thematic Overview" subtitle="Theme-level financials, utilisation, and outreach" badge="03" />

      {/* Theme Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="theme-cards-grid">
        {visibleCards.map(card => (
          <React.Fragment key={card.theme}>
          <div
            onClick={() => handleThemeClick(card.theme)}
            className={`bg-white rounded-xl border shadow-sm p-5 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer active:scale-[0.98] ${selectedTheme === card.theme ? "border-[#006837] ring-1 ring-[#006837]/20" : "border-slate-200"}`}
            data-testid={`theme-card-${card.theme}`}>
            <div className="flex items-start justify-between mb-3">
              <ThemeIcon theme={card.theme} size={16} />
              <span className="text-xs text-slate-600 bg-slate-50 px-2 py-0.5 rounded-full">{card.grants} grants</span>
            </div>
            <div className="font-semibold text-slate-900 text-sm mb-3 leading-tight">{displayTheme(card.theme)}</div>
            <div className="space-y-2">
              {sf && card.allocation_cr !== undefined ? (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Sanctioned</span>
                  <span className="text-sm font-bold text-slate-900">{fmtCr(card.allocation_cr)}</span>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Disbursed</span>
                  <span className="text-sm font-bold text-slate-900">{fmtCr(card.disbursed_cr)}</span>
                </div>
              )}
              {sf && card.pct_portfolio !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    % Portfolio
                    <Info size={10} className="text-slate-500" />
                  </span>
                  <span className="text-xs font-medium text-slate-700">{card.pct_portfolio}%</span>
                </div>
              )}
              {sf && card.avg_grant_size_cr !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Avg Grant Size</span>
                  <span className="text-xs text-slate-700">{fmtCr(card.avg_grant_size_cr)}</span>
                </div>
              )}
            </div>
            <div className={`flex items-center gap-1 mt-3 pt-2.5 border-t border-slate-100 text-[10px] font-medium ${selectedTheme === card.theme ? "text-[#006837]" : "text-[#006837]/60"}`}>
              <span>{selectedTheme === card.theme ? "Collapse" : "View details"}</span>
              <ChevronRight size={10} className={`transition-transform ${selectedTheme === card.theme ? "rotate-90" : ""}`} />
            </div>
          </div>
          {/* Expandable Detail Panel — inserted after the clicked card */}
          {selectedTheme === card.theme && (
            <ThemeDetailPanel detail={themeDetail} loading={detailLoading} themeColor={getThemeDotColor(card.theme)} onClose={() => { setSelectedTheme(null); setThemeDetail(null); }} onGrantsClick={scrollToGrantsTable} />
          )}
        </React.Fragment>
        ))}
        {themeCards.length === 0 && loading && (
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 h-44 animate-pulse" />
          ))
        )}
      </div>

      {/* Theme Comparison Table */}
      <SectionHeader title="Theme Comparison Table" collapsible defaultOpen={true}>
        <div className="flex justify-end mb-3">
          <button
            data-testid="export-theme-csv"
            onClick={() => {
              const headers = ["Theme","Grants","AWP (Cr)","Sanctioned (Cr)","Avg/Grant (Cr)","Disbursed (Cr)","Disb %","Districts","Blocks","Aspirational Dist.","Aspirational Blocks"];
              const rows = comparison.map(r => [
                r.theme, r.grants, r.awp_cr || "", r.allocation_cr || "", r.avg_grant_size_cr || "",
                r.disbursed_cr || "", r.disbursement_rate != null ? r.disbursement_rate + "%" : "",
                r.districts || "", r.blocks || "", r.aspirational_districts || "", r.aspirational_blocks || ""
              ]);
              const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "theme_comparison.csv"; a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#006837] hover:text-[#006837] transition-colors"
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="theme-comparison-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Theme</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2">Grants</th>
                {sf && <th className="text-right font-medium text-slate-500 uppercase py-2">AWP</th>}
                {sf && <th className="text-right font-medium text-slate-500 uppercase py-2">Sanctioned</th>}
                {sf && <th className="text-right font-medium text-slate-500 uppercase py-2">Avg/Grant</th>}
                <th className="text-right font-medium text-slate-500 uppercase py-2">Disbursed</th>
                {sf && <th className="text-right font-medium text-slate-500 uppercase py-2">Disb %</th>}
                <th className="text-right font-medium text-slate-500 uppercase py-2">Districts</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2">Blocks</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 group relative cursor-help">
                  <span className="border-b border-dotted border-slate-400">Aspirational Dist.</span>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity">
                    NITI Aayog 112 Aspirational Districts covered under this theme.
                  </div>
                </th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 group relative cursor-help">
                  <span className="border-b border-dotted border-slate-400">Aspirational Blocks</span>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity">
                    NITI Aayog 499 Aspirational Blocks covered under this theme.
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: getThemeDotColor(r.theme) }} />
                      <span className="font-medium text-slate-800">{r.theme}</span>
                    </div>
                  </td>
                  <td className="py-2 text-right">{r.grants}</td>
                  {sf && <td className="py-2 text-right font-medium text-slate-600">{r.awp_cr ? fmtCr(r.awp_cr) : "–"}</td>}
                  {sf && <td className="py-2 text-right font-medium text-[#006837]">{r.allocation_cr ? fmtCr(r.allocation_cr) : "–"}</td>}
                  {sf && <td className="py-2 text-right text-slate-600">{r.avg_grant_size_cr ? fmtCr(r.avg_grant_size_cr) : "–"}</td>}
                  <td className="py-2 text-right text-slate-600">{fmtCr(r.disbursed_cr)}</td>
                  {sf && <td className="py-2 text-right">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded ${r.disbursement_rate >= 70 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>}
                  <td className="py-2 text-right text-slate-500">{r.districts || "–"}</td>
                  <td className="py-2 text-right text-slate-500">{r.blocks || "–"}</td>
                  <td className="py-2 text-right">
                    {r.aspirational_districts > 0 ? (
                      <span className="text-[#006837] font-medium">{r.aspirational_districts}</span>
                    ) : "–"}
                  </td>
                  <td className="py-2 text-right">
                    {r.aspirational_blocks > 0 ? (
                      <span className="text-purple-600 font-medium">{r.aspirational_blocks}</span>
                    ) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionHeader>

      {/* Source of Fund × Portfolio Heatmap */}
      {trustMatrix.trusts.length > 0 && (
        <SectionHeader title="Source of Fund × Portfolio Heatmap" subtitle="Grants and allocation by funding trust and portfolio theme" collapsible defaultOpen={true}>
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setHeatmapToggle("grants")} data-testid="heatmap-toggle-grants"
              className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${heatmapToggle === "grants" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
              No. of Grants
            </button>
            <button onClick={() => setHeatmapToggle("alloc")} data-testid="heatmap-toggle-alloc"
              className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${heatmapToggle === "alloc" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
              Sanctioned (₹ Cr)
            </button>
          </div>
          {(() => {
            const allVals = trustMatrix.data.flatMap(row =>
              trustMatrix.trusts.map(t => heatmapToggle === "grants" ? row[`${t}_grants`] : row[`${t}_alloc`])
            ).filter(Boolean);
            const maxVal = Math.max(...allVals, 1);
            const colTotals = trustMatrix.trusts.map(t =>
              trustMatrix.data.reduce((sum, row) => sum + (heatmapToggle === "grants" ? (row[`${t}_grants`] || 0) : (row[`${t}_alloc`] || 0)), 0)
            );
            const grandTotal = colTotals.reduce((a, b) => a + b, 0);
            const fmtTotal = v => v > 0 ? (heatmapToggle === "grants" ? v : fmtCrNoSymbol(v)) : "—";
            return (
              <div className="overflow-x-auto" data-testid="trust-theme-heatmap">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left text-slate-500 font-medium py-2 pr-4 w-36">Portfolio</th>
                      {trustMatrix.trusts.map(t => (
                        <th key={t} className="text-center text-slate-600 font-semibold py-2 px-2 min-w-[72px]">{t}</th>
                      ))}
                      <th className="text-center text-slate-700 font-bold py-2 px-2 min-w-[72px] border-l border-slate-200">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trustMatrix.data.map((row, ri) => {
                      const rowTotal = trustMatrix.trusts.reduce((sum, t) =>
                        sum + (heatmapToggle === "grants" ? (row[`${t}_grants`] || 0) : (row[`${t}_alloc`] || 0)), 0
                      );
                      return (
                        <tr key={ri}>
                          <td className="py-1.5 pr-4 font-medium text-slate-700 whitespace-nowrap text-xs">
                            {displayTheme(row.theme)}
                          </td>
                          {trustMatrix.trusts.map(t => {
                            const val = heatmapToggle === "grants" ? row[`${t}_grants`] : row[`${t}_alloc`];
                            const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                            const display = val > 0 ? (heatmapToggle === "grants" ? val : fmtCrNoSymbol(val)) : "—";
                            return (
                              <td key={t} className="py-1.5 px-2 text-center rounded transition-colors"
                                style={{ backgroundColor: val > 0 ? `rgba(0,104,55,${intensity})` : "transparent",
                                         color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#006837" : "#CBD5E1" }}>
                                <span className="font-semibold">{display}</span>
                              </td>
                            );
                          })}
                          <td className="py-1.5 px-2 text-center border-l border-slate-200 bg-slate-50">
                            <span className="font-bold text-slate-700">{fmtTotal(rowTotal)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200">
                      <td className="py-2 pr-4 font-bold text-slate-700 text-xs">Total</td>
                      {colTotals.map((total, i) => (
                        <td key={i} className="py-2 px-2 text-center bg-slate-50">
                          <span className="font-bold text-slate-700">{fmtTotal(total)}</span>
                        </td>
                      ))}
                      <td className="py-2 px-2 text-center border-l border-slate-200 bg-slate-100">
                        <span className="font-bold text-[#006837]">{fmtTotal(grandTotal)}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
                <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-600">
                  <span>Intensity:</span>
                  {[0.1, 0.3, 0.6, 1].map(o => (
                    <span key={o} className="flex items-center gap-1">
                      <span className="w-4 h-3 rounded-sm inline-block" style={{ backgroundColor: `rgba(0,104,55,${o})` }} />
                      {o === 0.1 ? "Low" : o === 0.3 ? "Med" : o === 0.6 ? "High" : "Max"}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </SectionHeader>
      )}

      {/* Grants Table — visible when a theme is selected */}
      {selectedTheme && (
        <GrantsTableSection ref={grantsTableRef} filters={grantsTableFilters} extraTitle={selectedTheme} />
      )}
    </div>
  );
}
